import {
  FINISHED_STATUS,
  LABS_PERCENTAGE,
  OVERALL_STATUS,
  PASSED_STATUS,
  QUERIES_BY_TYPE,
  QUIZZES_PERCENTAGE,
  STARTED_STATUS,
  VIDEOS_PERCENTAGE,
} from 'constants/analytics';
import {
  getActiveUsersAction,
  getLabsAction,
  getSeriesByTypeAction,
} from 'contexts/analytics/actions';
import { ExportChartView } from './chartSpecs';

type ActionParams = Parameters<typeof getSeriesByTypeAction>[0];
type CounterQuery = { ACTIONS?: string; EXTERNAL_ACTION?: string; METADATA?: string };

// Read-only inputs for a capture run — built by the caller from SessionContext
// and the (unmutated) AnalyticsContext filters.
export interface ExportChartContext {
  date_from?: string;
  date_to?: string;
  period?: string;
  team_ids: string;
  user_id: string;
  account_id: string;
}

export interface ExportChartItem {
  date: string;
  count: number;
  finished: number;
  started: number;
  passed: number;
  watched: number;
  labs: number;
  videos: number;
  quizzes: number;
  total: number;
  licensed: number;
  percentage: number;
  standard: number;
  skill_dive: number;
  currentChart: ExportChartView;
}

interface SeriesPoint {
  date: string;
  count: number;
}

type SeriesArrays = Partial<Record<string, SeriesPoint[]>>;

// Which statuses to fetch per view and which chart array each fills. `items`
// is always fetched to supply the date backbone for the reshape.
const SERIES_FETCH_PLAN: Partial<Record<ExportChartView, { status: string; key: string }[]>> = {
  activities: [
    { status: OVERALL_STATUS.VALUE, key: 'items' },
    { status: LABS_PERCENTAGE.VALUE, key: 'labs' },
    { status: VIDEOS_PERCENTAGE.VALUE, key: 'videos' },
    { status: QUIZZES_PERCENTAGE.VALUE, key: 'quizzes' },
  ],
  videos: [
    { status: OVERALL_STATUS.VALUE, key: 'items' },
    { status: FINISHED_STATUS.VALUE, key: 'finished' },
    { status: STARTED_STATUS.VALUE, key: 'started' },
  ],
  videos_overall: [{ status: OVERALL_STATUS.VALUE, key: 'items' }],
  quizzes: [
    { status: OVERALL_STATUS.VALUE, key: 'items' },
    { status: FINISHED_STATUS.VALUE, key: 'finished' },
    { status: STARTED_STATUS.VALUE, key: 'started' },
    { status: PASSED_STATUS.VALUE, key: 'passed' },
  ],
};

function baseParams(ctx: ExportChartContext): Record<string, unknown> {
  return {
    date_to: ctx.date_to,
    date_from: ctx.date_from,
    period: ctx.period,
    team_ids: ctx.team_ids,
    user_id: ctx.user_id,
    account_id: ctx.account_id,
  };
}

export function buildSeriesParams(
  view: ExportChartView,
  status: string,
  ctx: ExportChartContext
): Record<string, unknown> {
  const counter = QUERIES_BY_TYPE[view] as Record<string, CounterQuery> | undefined;
  const query = counter?.[status] ?? {};
  return {
    ...baseParams(ctx),
    actions: query.ACTIONS,
    external_action: query.EXTERNAL_ACTION,
    metadata: query.METADATA,
  };
}

// Actions reject on failure in the app and resolve to `undefined` under test;
// treat both as a hard failure so the whole export fails (no partial bundle).
function requireResults<T = unknown>(res: unknown): T {
  if (res && typeof res === 'object' && 'results' in res) {
    return (res as { results: T }).results;
  }
  throw new Error('export_chart_fetch_failed');
}

export function buildExportChartItems(
  view: ExportChartView,
  data: SeriesArrays
): ExportChartItem[] {
  // Index each series by date once so the per-date lookups below are O(1).
  const byDate = (arr?: SeriesPoint[]): ((date: string) => number) => {
    const map = new Map<string, number>(arr?.map((p) => [p.date, p.count] as [string, number]));
    return (date) => map.get(date) ?? 0;
  };
  const finished = byDate(data.finished);
  const started = byDate(data.started);
  const passed = byDate(data.passed);
  const watched = byDate(data.watched);
  const labs = byDate(data.labs);
  const videos = byDate(data.videos);
  const quizzes = byDate(data.quizzes);
  const total = byDate(data.total);
  const licensed = byDate(data.licensed);
  const percentage = byDate(data.withActivity);
  const standard = byDate(data.standard);
  const skillDive = byDate(data.skill_dive);

  return (data.items ?? []).map(({ date, count }) => ({
    date,
    count,
    finished: finished(date),
    started: started(date),
    passed: passed(date),
    watched: watched(date),
    labs: labs(date),
    videos: videos(date),
    quizzes: quizzes(date),
    total: total(date),
    licensed: licensed(date),
    percentage: percentage(date),
    standard: standard(date),
    skill_dive: skillDive(date),
    currentChart: view,
  }));
}

/**
 * Fetch a single view's chart series in isolation and reshape into the flat
 * per-date rows the chart consumes. Uses only the pure analytics actions, so it
 * never mutates the visible dashboard state.
 */
export async function fetchExportChart(
  view: ExportChartView,
  ctx: ExportChartContext
): Promise<ExportChartItem[]> {
  if (view === 'users') {
    const res = await getActiveUsersAction({
      ...baseParams(ctx),
      filter_licensed_users: true,
    } as unknown as ActionParams);
    return buildExportChartItems(view, { items: requireResults<SeriesPoint[]>(res) });
  }

  if (view === 'labs') {
    const res = await getLabsAction(baseParams(ctx) as unknown as ActionParams);
    const results = requireResults<{
      items?: { date: string; count: number; standard: number; skill_dive: number }[];
    }>(res);
    const items = results.items ?? [];
    return buildExportChartItems(view, {
      items: items.map((i) => ({ date: i.date, count: i.count })),
      total: items.map((i) => ({ date: i.date, count: i.count })),
      standard: items.map((i) => ({ date: i.date, count: i.standard })),
      skill_dive: items.map((i) => ({ date: i.date, count: i.skill_dive })),
    });
  }

  const plan = SERIES_FETCH_PLAN[view] ?? [];
  const data: SeriesArrays = {};
  await Promise.all(
    plan.map(async ({ status, key }) => {
      const res = await getSeriesByTypeAction(
        buildSeriesParams(view, status, ctx) as unknown as ActionParams
      );
      data[key] = requireResults<SeriesPoint[]>(res);
    })
  );
  return buildExportChartItems(view, data);
}
